import { Enum } from '@lighthouse/tools'

import type {
    BlockAbstract,
    BlockType,
    ContainerBlockAbstract,
    FormContainerBlockAbstract,
    ViewBlockAbstract,
    ViewLayout,
    ViewStyles
} from '../abstracts'
import { AnyObject } from '../utils'
import type { HoverAnimation, LayerInViewAnimation, LoopAnimation, PanelChangeAnimation } from './animation'
import type {
    AutoLayout,
    BackgroundImageProtocol,
    BackgroundNoImageProtocol,
    BackgroundProtocol,
    Border,
    DesignStyle,
    LayoutPosition,
    LayoutSize,
    SizeConfigure
} from './design'
import type { FilterFormType } from './filter'
import type { CreatableProtocol } from './general'
import type { IconDecorationProtocol } from './icon'
import type { AlignMent } from './layout'
import type { RichTextContentProtocol } from './richtext'
import type { TextProtocol } from './text'
import type { VariableADTvalue } from './variable'
import type { ViewIndependentData } from './view'

export type BlockId = string

export type ScopeId = string
/**
 * 类 Block 对象协议
 */
export type BlockLikeProtocol = CreatableProtocol & {
    id: string
    type: string
}

export type ViewRecordPool = Record<string, ViewIndependentData>
/**
 * Block 协议
 */
export interface BlockProtocol extends BlockLikeProtocol {
    type: BlockType
    title: string
    content?: unknown
    config?: AnyObject
}

export const BLOCK_SIZE = Enum('xs', 'sm', 'md', 'lg', 'xl')
export type BlockSize = Enum<typeof BLOCK_SIZE>

export enum VISIBILITY_STATUS {
    MOUNT = 'mount',
    UNMOUNT = 'unmount',
    HIDDEN = 'hidden'
}

export const FilterVisibleModeEnum = Enum('HIDDEN', 'VISIBLE', 'FILTER')
export type FilterVisibleMode = Enum<typeof FilterVisibleModeEnum>
export const INTERACTION_STYLE_ENUM = Enum('hoverStyle', 'activeStyle', 'disabledStyle')
export type InteractionStyleType = Enum<typeof INTERACTION_STYLE_ENUM>
export interface BlockVisibilityProtocol {
    visibilityFilter?: FilterFormType
    visible?: FilterVisibleMode
    isMount?: boolean
}

export enum AnchorBehaviorEnum {
    INSTANT = 'instant',
    SMOOTH = 'smooth'
}

export type AnchorConfigProtocol = {
    anchorId?: string
    offset?: {
        x?: number
        y?: number
    }
    behavior?: AnchorBehaviorEnum
}

export type AnchorProtocol = {
    anchor?: AnchorConfigProtocol
}

// export interface DesignLayoutProtocol {
//     breakPoint: DevicePreviewEnum
//     layout: AutoLayout
//     size: LayoutSize
//     position?: LayoutPosition
// }

/** block公共的设计 布局配置 */
// export interface BreakPointDesignLayoutProtocol {
//     designLayoutMap: DesignLayoutProtocol[]
// }

// export interface BreakPointDesignProtocol {
//     design: {
//         desktop: DesignStyle
//         mobile: Partial<DesignStyle>
//     }
// }

// export interface BreakPointVisibilityProtocol {
//     visibility: {
//         desktop: BlockVisibilityProtocol
//         mobile: Partial<BlockVisibilityProtocol>
//     }
// }

export type AnimationBreakPoint = {
    layerInView?: LayerInViewAnimation
    hover?: HoverAnimation
    loop?: LoopAnimation
    panelChange?: PanelChangeAnimation
}

export type InteractionStyleBreakPointConfigProtocol = Partial<
    Record<
        InteractionStyleType,
        {
            open?: boolean
            design?: DesignStyle
        }
    >
>

/** 空状态插画显示模式 */
export const EMPTY_VIEW_IMAGE_MODE = Enum('default', 'hidden', 'custom')
export type EmptyViewImageMode = Enum<typeof EMPTY_VIEW_IMAGE_MODE>

/** 视图空状态配置协议 */
export interface EmptyViewBreakPointConfigProtocol {
    emptyView?: {
        /** 空状态文案（支持变量：组件数据、页面数据、当前登录用户数据） */
        title?: RichTextContentProtocol
        /** 文案颜色 */
        titleColor?: BackgroundNoImageProtocol
        /** 插画显示模式：default-默认 | hidden-隐藏 | custom-自定义 */
        imageMode?: EmptyViewImageMode
        /** 自定义图片（仅 imageMode 为 custom 时生效，大小限制1MB，格式为 image） */
        customImage?: BackgroundProtocol
    }
}

export interface BaseBreakPointConfigProtocol extends InteractionStyleBreakPointConfigProtocol {
    id: string
    // name: string

    visibility?: BlockVisibilityProtocol
    font?: TextProtocol
    size: LayoutSize
    layout?: AutoLayout
    position?: LayoutPosition
    design?: DesignStyle
    animation?: AnimationBreakPoint

    breakKeys?: string[]
}

export interface ContainerBreakPointConfigProtocol extends BaseBreakPointConfigProtocol {
    panel?: {
        defaultPanel: string
    }
}

/** 多维视图样式 */
type MultiDimensionalStyle = {
    fontSize?: SizeConfigure
    fontFamily?: string
    color?: BackgroundNoImageProtocol
    textAlign?: AlignMent
    background?: BackgroundNoImageProtocol
    size?: SizeConfigure
}

export interface ViewBreakPointConfigProtocol extends BaseBreakPointConfigProtocol, EmptyViewBreakPointConfigProtocol {
    /** 自定义视图 视图布局 */
    viewLayout?: ViewLayout
    /** 自定义视图 视图样式 周日历 */
    viewStyles?: ViewStyles

    /** 树组件时的展开收起控件配置 */
    tree?: {
        indent?: number
        align?: 'center' | 'top'
        topMargin?: number
        expandIcon?: { icon?: IconDecorationProtocol; color?: BackgroundNoImageProtocol }
        collapseIcon?: { icon?: IconDecorationProtocol; color?: BackgroundNoImageProtocol }
        defaultExpandAll?: boolean
    }

    /** 多维视图配置 */
    multiDimensionalStyle?: {
        row?: MultiDimensionalStyle
        col?: MultiDimensionalStyle
    }

    design?: DesignStyle
}

export interface TextBreakPointConfigProtocol extends BaseBreakPointConfigProtocol {
    design: DesignStyle
    font: TextProtocol & {
        advance?: string
        align?: AlignMent
    }
}

export type PartialExcludesIdAndName<T> = Partial<Omit<T, 'id'>> & {
    id: string
}

export interface ContainerBreakPointProtocol {
    breakPoint: ContainerBreakPointConfigProtocol
    breakPoints: PartialExcludesIdAndName<ContainerBreakPointConfigProtocol>[]
}

export interface ViewBreakPointProtocol {
    breakPoint: ViewBreakPointConfigProtocol
    breakPoints: PartialExcludesIdAndName<ViewBreakPointConfigProtocol>[]
}

export interface BaseBreakPointProtocol {
    /** 基础断点 */
    breakPoint: BaseBreakPointConfigProtocol
    /** 其他断点 */
    breakPoints: PartialExcludesIdAndName<BaseBreakPointConfigProtocol>[]
}

export interface TextBreakPointProtocol {
    breakPoint: TextBreakPointConfigProtocol
    breakPoints: PartialExcludesIdAndName<TextBreakPointConfigProtocol>[]
}

// export interface BlockCommonConfigProtocol extends BreakPointVisibilityProtocol, BreakPointDesignLayoutProtocol {}

export interface FieldBreakPointConfigProtocol extends BaseBreakPointConfigProtocol {
    design?: DesignStyle

    font?: TextProtocol

    // 成员字段样式
    personDesign?: { design?: DesignStyle } & InteractionStyleBreakPointConfigProtocol

    // 滑动条样式
    sliderDesign?: { design?: DesignStyle }

    // 已上传附件样式 & 宫格布局参数
    fileDesign?: {
        design?: DesignStyle
        // 宫格布局参数（仅 mode === 'grid' 时生效）
        gap?: SizeConfigure
        borderRadius?: number
        itemWidth?: SizeConfigure
        maxCount?: number
    } & InteractionStyleBreakPointConfigProtocol

    selectDesign?: { design?: DesignStyle } & InteractionStyleBreakPointConfigProtocol

    calendarHeader?: {
        fontFamily: string
        fontColor: BackgroundNoImageProtocol
        iconColor: BackgroundNoImageProtocol
    }
    calendarWeek?: {
        fontFamily: string
        fontColor: BackgroundNoImageProtocol
    }
    calendarDate?: {
        fontFamily: string
        currentMonthFontColor: BackgroundNoImageProtocol
        notCurrentMonthFontColor: BackgroundNoImageProtocol
        selectedFontColor: BackgroundNoImageProtocol
        selectedBackgroundColor: BackgroundNoImageProtocol
        hoverFontColor: BackgroundNoImageProtocol
        hoverBackgroundColor: BackgroundNoImageProtocol
    }
    calendar?: {
        /**
         * mode为week时生效，一屏展示的星期数量
         */
        weekShowCount?: number
        backgroundColor: BackgroundNoImageProtocol
        border?: Border
        describeFontColor: BackgroundNoImageProtocol
    }
}

export interface FieldBreakPointProtocol {
    breakPoint: FieldBreakPointConfigProtocol
    breakPoints: PartialExcludesIdAndName<FieldBreakPointConfigProtocol>[]
}

// ============== Block 上下文索引 ==============

/**
 * 单个 block 的运行时上下文信息
 * 包含父级引用和常用祖先容器的直接引用
 * @template T block 类型，默认为 BlockAbstract
 */
export interface BlockRuntimeContext<T extends BlockAbstract = BlockAbstract> {
    /** 当前节点对应的运行时 block（对同步组件实例会自动补全模板配置） */
    block: T
    /** 页面树中的原始 block（仅在同步组件实例根节点时存在） */
    originBlock?: BlockAbstract
    /** 同步组件模板中的 block（普通 block 为 undefined） */
    syncBlock?: BlockAbstract
    /** 索引用的唯一标识（包含 scope 等前缀，格式：scope@blockId 或 blockId） */
    uniqueKey: string
    /** 父节点的 uniqueKey（用于向上遍历） */
    parentUniqueKey?: string
    /** 所属的 FormContainer（最近的祖先） */
    formContainer?: FormContainerBlockAbstract
    /** 所属的 CustomView（最近的祖先） */
    customView?: ViewBlockAbstract
    /** 所属的 Container（最近的祖先） */
    container?: ContainerBlockAbstract
    /** 同步组件的 scope */
    scope?: string
    /** 多面板容器下，当前 block 所在面板的 id；用于派生 atom 按「当前面板」过滤（命名用 panelId 避免与视图 block 混淆） */
    panelId?: string
}

/**
 * 页面 block 上下文索引
 * 一次构建，多处复用，避免重复递归查找
 */
export interface PageBlockContextIndex {
    /** uniqueKey -> BlockRuntimeContext（核心索引） */
    contextMap: Map<string, BlockRuntimeContext>
    /** uniqueKey -> 运行时 BlockAbstract（快速查找，语义与 context.block 一致） */
    blockMap: Map<string, BlockAbstract>
    /** childUniqueKey -> parentUniqueKey（向上遍历） */
    parentMap: Map<string, string>
}
